import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiTrash2, FiRefreshCw, FiLogOut, FiAlertCircle, FiUser, FiMail, FiLock, FiCheck, FiX, FiHelpCircle, FiEdit, FiSave, FiSearch } from 'react-icons/fi';
import Header from './Header';
import axios from 'axios';
import { endSession } from '../services/session';
import {
  calculateOnboardingProgress,
  getAppleAccountEmail,
  getAppleAccountEmailUpdatedAt,
  getOnboardingTaskName,
  isOnboardingTaskComplete
} from '../utils/onboardingTasks';

const AdminPage = () => {
  const USERS_PER_PAGE = 20;
  // User management state
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', password: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('lastLogin'); // 'lastLogin', 'email', 'progress'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  // Question management state
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({
    title: '',
    description: '',
    type: 'text',
    options: [''],
    helpText: '',
    order: 0
  });
  const [editingQuestion, setEditingQuestion] = useState(null); // Stores the question being edited

  // General state
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState({ show: false, id: null, action: null, type: null });
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'questions'

  // Add state for password reset
  const [resetSuccess, setResetSuccess] = useState(null);

  // Add mounting state to prevent framer-motion errors on initial load
  const [isMounted, setIsMounted] = useState(false);

  // Helper function to convert UTC timestamps to Eastern Time
  const formatEasternTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    // Convert to Eastern Time (America/New_York)
    const options = {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  };

  // Mount effect - sets isMounted flag after component is in DOM
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      setIsMounted(true);
    });

    // Add global keyboard event listener to debug
    const handleKeyDown = (e) => {
      // Only log if not typing in an input
      if (document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
        console.log('Key pressed outside input:', e.key, 'Active element:', document.activeElement.tagName);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Data loading effect - loads data on mount and when tab changes
  useEffect(() => {
    if (isMounted) {
      if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'questions') {
        fetchQuestions();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, activeTab]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredUsers = useMemo(() => {
    // First filter by search query
    let filtered = users;
    if (normalizedQuery) {
      filtered = users.filter((user) => (
        user.email?.toLowerCase().includes(normalizedQuery)
        || getAppleAccountEmail(user).includes(normalizedQuery)
      ));
    }

    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'lastLogin':
          const aLogin = a.lastLogin || (a.logins && a.logins.length > 0 ? a.logins[a.logins.length - 1].loginTime : null);
          const bLogin = b.lastLogin || (b.logins && b.logins.length > 0 ? b.logins[b.logins.length - 1].loginTime : null);
          compareValue = (aLogin ? new Date(aLogin).getTime() : 0) - (bLogin ? new Date(bLogin).getTime() : 0);
          break;

        case 'email':
          compareValue = (a.email || '').localeCompare(b.email || '');
          break;

        case 'progress':
          const aProgress = calculateOnboardingProgress(a.tasks);
          const bProgress = calculateOnboardingProgress(b.tasks);
          compareValue = aProgress - bProgress;
          break;

        default:
          return 0;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return sorted;
  }, [users, normalizedQuery, sortBy, sortOrder]);

  const totalUsers = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalUsers / USERS_PER_PAGE));
  const activePage = Math.min(currentPage, totalPages);
  const startIndex = (activePage - 1) * USERS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + USERS_PER_PAGE);
  const showingFrom = totalUsers === 0 ? 0 : startIndex + 1;
  const showingTo = totalUsers === 0 ? 0 : startIndex + paginatedUsers.length;
  const hasSearch = normalizedQuery.length > 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [normalizedQuery]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const goToPage = (pageNumber) => {
    const clampedPage = Math.min(Math.max(pageNumber, 1), totalPages);
    setCurrentPage(clampedPage);
  };

  // --- User Management Functions ---
  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null); // Clear any previous errors
    console.log('Fetching users...'); // Debug log
    console.log('API URL:', '/api/xapi');
    console.log('Current origin:', window.location.origin);
    try {
      const response = await axios.post('/api/xapi', {
        action: 'getAllUsers'
      });
      console.log('Response received:', response);
      console.log('Users fetched:', response.data.users?.length || 0); // Debug log
      const fetchedUsers = response.data.users || [];

      // Safety check: Don't update if we got an empty array unexpectedly
      if (fetchedUsers.length === 0 && users.length > 0) {
        console.warn('Received empty users array when we already have users. Keeping existing users.');
        setError('Warning: Server returned no users. Keeping cached data.');
      } else {
        setUsers(fetchedUsers);
        setError(null);
        console.log('Users state updated with', fetchedUsers.length, 'users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        request: error.request,
        config: error.config
      });
      setError(`Unable to fetch users: ${error.message}`);
      // Don't clear users on error - keep existing data
      console.log('Keeping existing users due to fetch error');
    } finally {
      setIsLoading(false);
    }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password) {
      setError('Email and password are required');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post('/api/xapi', {
        action: 'createUser',
        email: newUser.email,
        password: newUser.password,
      });
      setNewUser({ email: '', password: '' });
      setSuccess(`User ${newUser.email} created successfully!`);
      setTimeout(() => setSuccess(null), 3000);
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      setError('Unable to create user. Please check if this email already exists.');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteUser = async (email) => {
    setIsLoading(true);
    try {
      await axios.post('/api/xapi', {
        action: 'deleteUser',
        email: email,
      });
      setSuccess(`User ${email} deleted successfully!`);
      setTimeout(() => setSuccess(null), 3000);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Unable to delete user. Please try again later.');
    } finally {
      setIsLoading(false);
      setShowConfirmation({ show: false, id: null, action: null, type: null });
    }
  };

  const resetUserTasks = async (email) => {
    setIsLoading(true);
    try {
      await axios.post('/api/xapi', {
        action: 'resetTasks',
        email: email,
      });
      setSuccess(`Tasks for ${email} have been reset!`);
      setTimeout(() => setSuccess(null), 3000);
      fetchUsers();
    } catch (error) {
      console.error('Error resetting tasks:', error);
      setError('Unable to reset tasks. Please try again later.');
    } finally {
      setIsLoading(false);
      setShowConfirmation({ show: false, id: null, action: null, type: null });
    }
  };

  // Add function to reset user password
  const resetUserPassword = async (email) => {
    setIsLoading(true);
    try {
      await axios.post('/api/xapi', {
        action: 'adminResetUserPassword',
        email: email,
      });
      setResetSuccess(`Password reset email sent to ${email}`);
      setSuccess(null); // Clear other success messages
      setTimeout(() => setResetSuccess(null), 5000);
    } catch (error) {
      console.error('Error resetting user password:', error);
      setError('Unable to reset password. Please try again later.');
    } finally {
      setIsLoading(false);
      setShowConfirmation({ show: false, id: null, action: null, type: null });
    }
  };

  // --- Question Management Functions --- 
  const fetchQuestions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post('/api/xapi', {
        action: 'getQuestions'
      });
      setQuestions(response.data.questions || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError('Unable to fetch questions. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetQuestionForm = () => {
    setNewQuestion({
      title: '',
      description: '',
      type: 'text',
      options: [''],
      helpText: '',
      order: 0
    });
    setEditingQuestion(null);
  };

  const handleQuestionSubmit = async () => {
    if (!newQuestion.title) {
      setError('Question title is required');
      return;
    }

    // Prepare question data, filtering empty options if applicable
    let questionData = { ...newQuestion };
    if (questionData.type === 'multiple_choice' || questionData.type === 'checkbox') {
      questionData.options = questionData.options.filter(option => option && option.trim()).map(opt => opt.trim());
      if (questionData.options.length === 0 && (questionData.type === 'multiple_choice' || questionData.type === 'checkbox')) {
        setError('Please add at least one valid option for multiple choice/checkbox questions.');
        return;
      }
    } else {
      delete questionData.options; // Remove options array if not multiple choice/checkbox
    }
    // Ensure order is a number
    questionData.order = Number(questionData.order) || 0;

    setIsLoading(true);
    try {
      let actionType = editingQuestion ? 'updateQuestion' : 'createQuestion';
      let successMessage = '';

      const payload = { action: actionType, questionData };
      if (editingQuestion) {
        payload.questionId = editingQuestion._id;
        successMessage = `Question "${questionData.title}" updated successfully!`;
      } else {
        successMessage = `Question "${questionData.title}" created successfully!`;
      }

      await axios.post('/api/xapi', payload);

      setSuccess(successMessage);
      resetQuestionForm();
      fetchQuestions(); // Refresh the list
      setTimeout(() => setSuccess(null), 3000);

    } catch (error) {
      console.error('Error saving question:', error);
      setError('Unable to save question. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setNewQuestion({
      title: question.title,
      description: question.description || '',
      type: question.type || 'text',
      // Ensure options is an array, even if it's missing or empty in the data
      options: Array.isArray(question.options) ? (question.options.length > 0 ? question.options : ['']) : [''], 
      helpText: question.helpText || '',
      order: question.order || 0
    });
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top to see the form
  };

  const deleteQuestion = async (questionId) => {
    setIsLoading(true);
    try {
      await axios.post('/api/xapi', {
        action: 'deleteQuestion',
        questionId
      });
      setSuccess('Question deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
      fetchQuestions(); // Refresh the list
    } catch (error) {
      console.error('Error deleting question:', error);
      setError('Unable to delete question. Please try again later.');
    } finally {
      setIsLoading(false);
      setShowConfirmation({ show: false, id: null, action: null, type: null });
    }
  };

  // --- Form Input Handlers for Questions ---
  const handleAddOption = () => {
    setNewQuestion({
      ...newQuestion,
      options: [...newQuestion.options, '']
    });
  };

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...newQuestion.options];
    updatedOptions[index] = value;
    setNewQuestion({
      ...newQuestion,
      options: updatedOptions
    });
  };

  const handleRemoveOption = (index) => {
    // Prevent removing the last option input field
    if (newQuestion.options.length <= 1) return; 
    const updatedOptions = newQuestion.options.filter((_, i) => i !== index);
    setNewQuestion({
      ...newQuestion,
      options: updatedOptions
    });
  };

  // --- General Functions ---
  const confirmAction = (id, action, type) => {
    // Prevent action if user is selecting text
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      console.log('User is selecting text, ignoring button click');
      return;
    }
    setShowConfirmation({ show: true, id: id, action: action, type: type });
  };

  // Debug: Log when users change
  useEffect(() => {
    console.log('Total users:', users.length);
  }, [users.length]);

  const getProgressColor = (progress) => {
    if (progress < 30) return 'bg-red-500';
    if (progress < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Show nothing until component is properly mounted to prevent framer-motion errors
  if (!isMounted) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
          <div className="text-center">
            <div className="spinner mb-3 mx-auto"></div>
            <p className="text-gray-500">Loading...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col md:flex-row md:items-center justify-between mb-8"
          >
            <div>
              <h2 className="text-3xl font-bold text-brandDark font-heading">Admin Dashboard</h2>
              <p className="text-gray-600">Manage users and track their onboarding progress</p>
            </div>
            <motion.button 
              onClick={async () => {
                await endSession();
                window.location.href = '/admin-login';
              }}
              className="mt-4 md:mt-0 btn btn-danger flex items-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiLogOut className="mr-2" />
              Logout
            </motion.button>
          </motion.div>
          
          {error && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-red-50 border-l-4 border-danger p-4 rounded-lg mb-6 flex items-center"
            >
              <FiAlertCircle className="text-danger mr-3 text-xl" />
              <p className="text-danger">{error}</p>
            </motion.div>
          )}

          {success && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-green-50 border-l-4 border-success p-4 rounded-lg mb-6 flex items-center"
            >
              <FiCheck className="text-success mr-3 text-xl" />
              <p className="text-success">{success}</p>
            </motion.div>
          )}
          
          {resetSuccess && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-blue-50 border-l-4 border-brandBlue p-4 rounded-lg mb-6 flex items-center"
            >
              <FiMail className="text-brandBlue mr-3 text-xl" />
              <p className="text-brandBlue">{resetSuccess}</p>
            </motion.div>
          )}
          
          {/* Tabs for navigation */}
          <div className="flex border-b border-gray-200 mb-8">
            <motion.button
              className={`py-3 px-6 flex items-center font-medium ${activeTab === 'users' ? 'text-brandBlue border-b-2 border-brandBlue' : 'text-gray-600 hover:text-brandBlue'}`}
              onClick={() => setActiveTab('users')}
              whileHover={{ y: -2 }}
            >
              <FiUser className="mr-2" /> Users
            </motion.button>
            <motion.button
              className={`py-3 px-6 flex items-center font-medium ${activeTab === 'questions' ? 'text-brandBlue border-b-2 border-brandBlue' : 'text-gray-600 hover:text-brandBlue'}`}
              onClick={() => setActiveTab('questions')}
              whileHover={{ y: -2 }}
            >
              <FiHelpCircle className="mr-2" /> Questions
            </motion.button>
          </div>
          
          {/* Users Tab Content */}
          {activeTab === 'users' && (
            <>
              {/* Create User Card */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="card mb-8"
              >
                <h3 className="text-xl font-semibold mb-4 font-heading text-brandDark">Create New User</h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Email Address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiMail className="text-gray-400" />
                      </div>
                      <input
                        type="email"
                        placeholder="Enter email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        className="form-input pl-10"
                        name="new-user-email"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        data-lpignore="true"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiLock className="text-gray-400" />
                      </div>
                      <input
                        type="password"
                        placeholder="Create password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        className="form-input pl-10"
                        name="new-user-password"
                        autoComplete="new-password"
                        autoCorrect="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        data-lpignore="true"
                        data-1p-ignore="true"
                      />
                    </div>
                  </div>
                </div>
                <motion.button
                  onClick={createUser}
                  className="btn btn-primary flex items-center justify-center"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="spinner mr-2"></div>
                  ) : (
                    <FiPlus className="mr-2" />
                  )}
                  {isLoading ? 'Creating User...' : 'Create User'}
                </motion.button>
              </motion.div>
              
              {/* User Management Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="card"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold font-heading text-brandDark">Manage Users</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {totalUsers} user{totalUsers !== 1 ? 's' : ''}
                      {hasSearch && ` (filtered from ${users.length})`}
                    </p>
                  </div>

                  <div className="mt-3 md:mt-0 w-full md:w-auto flex flex-col md:flex-row md:items-center gap-3">
                    <div className="relative w-full md:w-64">
                      <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="search"
                        name="user-search"
                        placeholder="Search login or Apple email"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="form-input pl-10"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        data-lpignore="true"
                        data-1p-ignore="true"
                      />
                    </div>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="form-input w-full md:w-auto"
                      title="Sort users by"
                    >
                      <option value="lastLogin">Last Login</option>
                      <option value="email">Email</option>
                      <option value="progress">Progress</option>
                    </select>
                    <button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                      title={sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </button>
                    <motion.button
                      onClick={fetchUsers}
                      className="bg-brandBlue text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-600 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Refresh user list"
                    >
                      <FiRefreshCw className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </motion.button>
                  </div>
                </div>
                
                {isLoading && totalUsers === 0 ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="spinner mr-3"></div>
                    <p className="text-gray-500">Loading users...</p>
                  </div>
                ) : totalUsers > 0 ? (
                  <div className="space-y-4">
                    {paginatedUsers.map(user => (
                      <div
                        key={user.email}
                        className="bg-white border border-gray-200 rounded-lg shadow-sm p-5"
                        style={{ userSelect: 'text', cursor: 'default' }}
                      >
                          <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                            <div className="mb-3 md:mb-0" style={{ userSelect: 'text' }}>
                              <div className="flex items-center">
                                <FiUser className="text-brandBlue mr-2 text-xl" style={{ userSelect: 'none' }} />
                                <h4 className="text-lg font-semibold text-gray-800" style={{ userSelect: 'text', cursor: 'text' }}>{user.email}</h4>
                              </div>
                              <div className="text-sm text-gray-500 mt-1" style={{ userSelect: 'text', cursor: 'text' }}>Role: {user.role || 'User'}</div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <motion.button
                                onClick={() => confirmAction(user.email, 'reset-password', 'user')}
                                className="bg-blue-50 text-brandBlue border border-brandBlue px-3 py-1 rounded-md flex items-center text-sm hover:bg-blue-100 transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <FiLock className="mr-1" /> Reset Password
                              </motion.button>
                              <motion.button
                                onClick={() => confirmAction(user.email, 'reset', 'user')}
                                className="bg-yellow-50 text-warning border border-warning px-3 py-1 rounded-md flex items-center text-sm hover:bg-yellow-100 transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <FiRefreshCw className="mr-1" /> Reset Tasks
                              </motion.button>
                              <motion.button
                                onClick={() => confirmAction(user.email, 'delete', 'user')}
                                className="bg-red-50 text-danger border border-danger px-3 py-1 rounded-md flex items-center text-sm hover:bg-red-100 transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <FiTrash2 className="mr-1" /> Delete
                              </motion.button>
                            </div>
                          </div>

                          <div
                            className={`mb-4 flex items-start rounded-lg border p-3 ${
                              getAppleAccountEmail(user)
                                ? 'border-blue-200 bg-blue-50'
                                : 'border-amber-200 bg-amber-50'
                            }`}
                            style={{ userSelect: 'text' }}
                          >
                            <FiMail className="mr-3 mt-0.5 flex-shrink-0 text-brandBlue" />
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-700">
                                ACT App — Apple Account email
                              </div>
                              {getAppleAccountEmail(user) ? (
                                <>
                                  <a
                                    href={`mailto:${getAppleAccountEmail(user)}`}
                                    className="break-all text-sm font-medium text-brandBlue hover:underline"
                                  >
                                    {getAppleAccountEmail(user)}
                                  </a>
                                  {getAppleAccountEmailUpdatedAt(user) && (
                                    <div className="mt-1 text-xs text-gray-500">
                                      Last saved {formatEasternTime(getAppleAccountEmailUpdatedAt(user))} ET
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="text-sm font-medium text-amber-800">
                                  Not submitted yet
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 mb-4" style={{ userSelect: 'text' }}>
                            <div className="bg-gray-50 p-3 rounded-lg" style={{ userSelect: 'text' }}>
                              <div className="text-sm text-gray-500" style={{ userSelect: 'text' }}>Progress</div>
                              <div className="flex items-center mt-1">
                                <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" style={{ userSelect: 'none' }}>
                                  {(() => {
                                    const progressPercentage = Math.round(
                                      calculateOnboardingProgress(user.tasks)
                                    );
                                    return (
                                      <>
                                        <div className={`h-2.5 rounded-full ${getProgressColor(progressPercentage)}`} style={{ width: `${progressPercentage}%`, userSelect: 'none' }}></div>
                                      </>
                                    );
                                  })()}
                                </div>
                                {(() => {
                                  const progressPercentage = Math.round(
                                    calculateOnboardingProgress(user.tasks)
                                  );
                                  return <span className="text-sm font-medium" style={{ userSelect: 'text', cursor: 'text' }}>{progressPercentage}%</span>;
                                })()}
                              </div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg" style={{ userSelect: 'text' }}>
                              <div className="text-sm text-gray-500" style={{ userSelect: 'text' }}>Last Login (ET)</div>
                              <div className="text-sm font-medium mt-1" style={{ userSelect: 'text', cursor: 'text' }}>
                                {user.lastLogin
                                  ? formatEasternTime(user.lastLogin)
                                  : user.logins && user.logins.length > 0
                                  ? formatEasternTime(user.logins[user.logins.length - 1].loginTime)
                                  : 'Never'}
                              </div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg" style={{ userSelect: 'text' }}>
                              <div className="text-sm text-gray-500" style={{ userSelect: 'text' }}>Login Count</div>
                              <div className="text-sm font-medium mt-1" style={{ userSelect: 'text', cursor: 'text' }}>{user.loginCount || 0}</div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg" style={{ userSelect: 'text' }}>
                              <div className="text-sm text-gray-500" style={{ userSelect: 'text' }}>Time Spent</div>
                              <div className="text-sm font-medium mt-1" style={{ userSelect: 'text', cursor: 'text' }}>{Math.floor((user.totalTimeSpent || 0) / 60)} min</div>
                            </div>
                          </div>

                          {/* IP Address & Location Info */}
                          {user.lastIpAddress && (
                            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4" style={{ userSelect: 'text' }}>
                              <h5 className="font-medium text-gray-700 mb-2 flex items-center" style={{ userSelect: 'text' }}>
                                <svg className="w-4 h-4 mr-2 text-brandBlue" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                                </svg>
                                Last Login Location
                              </h5>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div style={{ userSelect: 'text' }}>
                                  <span className="text-gray-600">IP Address:</span>
                                  <div className="font-mono font-medium" style={{ userSelect: 'text', cursor: 'text' }}>{user.lastIpAddress}</div>
                                </div>
                                {user.logins && user.logins.length > 0 && user.logins[user.logins.length - 1].location && (
                                  <>
                                    <div style={{ userSelect: 'text' }}>
                                      <span className="text-gray-600">City:</span>
                                      <div className="font-medium" style={{ userSelect: 'text', cursor: 'text' }}>
                                        {user.logins[user.logins.length - 1].location.city || 'Unknown'}
                                      </div>
                                    </div>
                                    <div style={{ userSelect: 'text' }}>
                                      <span className="text-gray-600">Region:</span>
                                      <div className="font-medium" style={{ userSelect: 'text', cursor: 'text' }}>
                                        {user.logins[user.logins.length - 1].location.region || 'Unknown'}
                                      </div>
                                    </div>
                                    <div style={{ userSelect: 'text' }}>
                                      <span className="text-gray-600">Country:</span>
                                      <div className="font-medium" style={{ userSelect: 'text', cursor: 'text' }}>
                                        {user.logins[user.logins.length - 1].location.country || 'Unknown'}
                                      </div>
                                    </div>
                                    <div style={{ userSelect: 'text' }}>
                                      <span className="text-gray-600">ISP:</span>
                                      <div className="font-medium text-xs" style={{ userSelect: 'text', cursor: 'text' }}>
                                        {user.logins[user.logins.length - 1].location.isp || 'Unknown'}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Task Progress */}
                          <div className="mt-4" style={{ userSelect: 'text' }}>
                            <h5 className="font-medium text-gray-700 mb-3" style={{ userSelect: 'text' }}>Task Progress</h5>
                            <div className="space-y-2">
                              {user.tasks && user.tasks.length > 0 ? (
                                user.tasks.map((task, index) => {
                                  const taskIsComplete = isOnboardingTaskComplete(task, index);

                                  return (
                                    <div key={index} className="flex justify-between items-center text-sm" style={{ userSelect: 'text' }}>
                                      <span className="flex items-center" style={{ userSelect: 'text', cursor: 'text' }}>
                                        {taskIsComplete ? (
                                          <FiCheck className="text-success mr-2" style={{ userSelect: 'none' }} />
                                        ) : (
                                          <FiX className="text-gray-400 mr-2" style={{ userSelect: 'none' }} />
                                        )}
                                        {getOnboardingTaskName(task, index)}
                                      </span>
                                      <span className={taskIsComplete ? 'text-success' : 'text-gray-500'} style={{ userSelect: 'text', cursor: 'text' }}>
                                        {taskIsComplete ? 'Completed' : 'Pending'}
                                        {task.timestamp && taskIsComplete && (
                                          <span className="text-xs text-gray-500 ml-2" style={{ userSelect: 'text', cursor: 'text' }}>
                                            {formatEasternTime(task.timestamp)}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  );
                                })
                              ) : (
                                <p className="text-gray-500 text-sm" style={{ userSelect: 'text' }}>No tasks found for this user.</p>
                              )}
                            </div>
                          </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      {hasSearch ? 'No users match your search.' : 'No users found.'}
                    </p>
                  </div>
                )}

                {totalUsers > 0 && (
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-6 gap-3">
                    <p className="text-sm text-gray-500">
                      Showing {showingFrom}-{showingTo} of {totalUsers} {hasSearch ? 'matching users' : 'users'}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => goToPage(activePage - 1)}
                        className={`px-3 py-1 rounded-md border ${activePage === 1 ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-brandBlue text-brandBlue hover:bg-blue-50'}`}
                        disabled={activePage === 1}
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-500">
                        Page {activePage} of {totalPages}
                      </span>
                      <button
                        onClick={() => goToPage(activePage + 1)}
                        className={`px-3 py-1 rounded-md border ${activePage === totalPages || totalUsers === 0 ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-brandBlue text-brandBlue hover:bg-blue-50'}`}
                        disabled={activePage === totalPages || totalUsers === 0}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            </>
          )}
          
          {/* Questions Tab Content */}
          {activeTab === 'questions' && (
            <>
              {/* Create/Edit Question Card */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="card mb-8"
              >
                <h3 className="text-xl font-semibold mb-4 font-heading text-brandDark">
                  {editingQuestion ? 'Edit Question' : 'Create New Question'}
                </h3>
                
                {/* Question Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  {/* Title */}
                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Title <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter question title"
                      value={newQuestion.title}
                      onChange={(e) => setNewQuestion({ ...newQuestion, title: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  
                  {/* Description */}
                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      placeholder="Enter question description (optional)"
                      value={newQuestion.description}
                      onChange={(e) => setNewQuestion({ ...newQuestion, description: e.target.value })}
                      className="form-input h-24"
                    />
                  </div>

                  {/* Type */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <select
                      value={newQuestion.type}
                      onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value, options: [''] })} // Reset options on type change
                      className="form-input"
                    >
                      <option value="text">Text</option>
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="checkbox">Checkbox</option>
                    </select>
                  </div>
                  
                  {/* Order */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Order</label>
                    <input
                      type="number"
                      placeholder="Enter display order (e.g., 1)"
                      value={newQuestion.order}
                      onChange={(e) => setNewQuestion({ ...newQuestion, order: parseInt(e.target.value) || 0 })}
                      className="form-input"
                    />
                  </div>

                  {/* Options (Conditional) */}
                  {(newQuestion.type === 'multiple_choice' || newQuestion.type === 'checkbox') && (
                    <div className="md:col-span-2 space-y-3">
                      <label className="block text-sm font-medium text-gray-700">Options</label>
                      {newQuestion.options.map((option, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            placeholder={`Option ${index + 1}`}
                            value={option}
                            onChange={(e) => handleOptionChange(index, e.target.value)}
                            className="form-input flex-grow"
                          />
                          <button
                            onClick={() => handleRemoveOption(index)}
                            className={`text-gray-400 hover:text-danger ${newQuestion.options.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={newQuestion.options.length <= 1}
                            title="Remove Option"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={handleAddOption}
                        className="btn btn-secondary text-sm py-1 px-3 flex items-center"
                      >
                        <FiPlus className="mr-1" /> Add Option
                      </button>
                    </div>
                  )}

                  {/* Help Text */}
                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Help Text</label>
                    <textarea
                      placeholder="Provide optional help or context (optional)"
                      value={newQuestion.helpText}
                      onChange={(e) => setNewQuestion({ ...newQuestion, helpText: e.target.value })}
                      className="form-input h-20"
                    />
                  </div>
                </div>
                
                {/* Form Actions */}
                <div className="flex items-center space-x-3">
                  <motion.button
                    onClick={handleQuestionSubmit}
                    className="btn btn-primary flex items-center justify-center"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="spinner mr-2"></div>
                    ) : editingQuestion ? (
                      <FiSave className="mr-2" />
                    ) : (
                      <FiPlus className="mr-2" />
                    )}
                    {isLoading ? 'Saving...' : (editingQuestion ? 'Update Question' : 'Create Question')}
                  </motion.button>
                  {editingQuestion && (
                    <motion.button
                      onClick={resetQuestionForm} // Use the reset function
                      className="btn btn-secondary flex items-center justify-center"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={isLoading}
                    >
                      <FiX className="mr-2" /> Cancel Edit
                    </motion.button>
                  )}
                </div>
              </motion.div>

              {/* Questions List Card */} 
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="card"
              >
                <h3 className="text-xl font-semibold font-heading text-brandDark mb-6">Manage Questions</h3>

                {isLoading && questions.length === 0 ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="spinner mr-3"></div>
                    <p className="text-gray-500">Loading questions...</p>
                  </div>
                ) : questions.length > 0 ? (
                  <div className="space-y-4">
                    {/* Sort questions by order before mapping */}
                    {[...questions].sort((a, b) => (a.order || 0) - (b.order || 0)).map(question => (
                      <div
                        key={question._id}
                        className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-card transition-all duration-200 hover:-translate-y-0.5 p-5"
                      >
                        <div className="flex flex-col md:flex-row justify-between">
                          {/* Question Info */}
                          <div className="mb-4 md:mb-0 md:pr-4 flex-grow">
                            <div className="flex items-center mb-2">
                              <span className="text-xs font-semibold bg-brandLight text-brandDark px-2 py-0.5 rounded mr-2">Order: {question.order || 'N/A'}</span>
                              <h4 className="text-lg font-semibold text-gray-800">{question.title}</h4>
                            </div>
                            {question.description && <p className="text-sm text-gray-600 mb-1">{question.description}</p>}
                            <p className="text-xs text-gray-500">Type: <span className="font-medium capitalize">{question.type.replace('_', ' ')}</span></p>
                            {(question.type === 'multiple_choice' || question.type === 'checkbox') && question.options && (
                              <div className="text-xs text-gray-500 mt-1">
                                Options: <span className="italic">{question.options.join(', ')}</span>
                              </div>
                            )}
                            {question.helpText && <p className="text-xs text-gray-500 mt-1">Help: <span className="italic">{question.helpText}</span></p>}
                            <p className="text-xs text-gray-500 mt-1">ID: {question._id}</p>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex-shrink-0 flex flex-col md:flex-row md:items-start gap-2">
                             <motion.button
                              onClick={() => handleEditQuestion(question)}
                              className="bg-blue-50 text-brandBlue border border-brandBlue px-3 py-1 rounded-md flex items-center text-sm hover:bg-blue-100 transition-colors w-full md:w-auto justify-center"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <FiEdit className="mr-1" /> Edit
                            </motion.button>
                            <motion.button
                              onClick={() => confirmAction(question._id, 'delete', 'question')} // Use confirmAction
                              className="bg-red-50 text-danger border border-danger px-3 py-1 rounded-md flex items-center text-sm hover:bg-red-100 transition-colors w-full md:w-auto justify-center"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <FiTrash2 className="mr-1" /> Delete
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No questions found. Create one using the form above.</p>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </div>
      </div>
      
      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation.show && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full"
            >
              <h3 className="text-xl font-bold mb-4 text-gray-800">Confirm Action</h3>
              <p className="mb-6 text-gray-600">
                {showConfirmation.action === 'delete' 
                  ? `Are you sure you want to delete ${showConfirmation.type === 'user' ? 'user' : 'question'} ${showConfirmation.id}? This action cannot be undone.`
                  : showConfirmation.action === 'reset'
                  ? `Are you sure you want to reset all tasks for ${showConfirmation.type === 'user' ? 'user' : 'question'} ${showConfirmation.id}?`
                  : showConfirmation.action === 'reset-password'
                  ? `Are you sure you want to send a password reset email to ${showConfirmation.id}?`
                  : `Are you sure you want to proceed?`
                }
              </p>
              <div className="flex justify-end space-x-3">
                <motion.button
                  onClick={() => setShowConfirmation({ show: false, id: null, action: null, type: null })}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={() => {
                    if (showConfirmation.action === 'delete') {
                      if (showConfirmation.type === 'user') {
                        deleteUser(showConfirmation.id);
                      } else if (showConfirmation.type === 'question') {
                        deleteQuestion(showConfirmation.id);
                      }
                    } else if (showConfirmation.action === 'reset') {
                      if (showConfirmation.type === 'user') {
                        resetUserTasks(showConfirmation.id);
                      } else if (showConfirmation.type === 'question') {
                        resetQuestionForm();
                      }
                    } else if (showConfirmation.action === 'reset-password') {
                      resetUserPassword(showConfirmation.id);
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-white ${
                    showConfirmation.action === 'delete' 
                      ? 'bg-danger hover:bg-red-600' 
                      : showConfirmation.action === 'reset'
                      ? 'bg-warning hover:bg-yellow-600'
                      : 'bg-brandBlue hover:bg-blue-600'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Confirm
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminPage;
